/**
 * Customer PWA analytics manager — Analytics Core Engine v2.2.5 §2.3,
 * §2.6.1, §9.2.
 *
 * Single analytics session per PWA tab. The PWA shares
 * `customer_checkout_sessions.publicId` with the analytics session ID
 * when a checkout context exists, so the same UUID identifies the
 * commerce session and the analytics session (plan §5 / §9.4.1).
 *
 * Lifecycle:
 *   - `track()` lazily starts a session on first call.
 *   - `completeSession()` marks the session finished (e.g. payment
 *     confirmed → receipt).
 *   - `abandonSession()` records an explicit abandon with a reason.
 *   - `pagehide` / `visibilitychange` (hidden) fire a `sendBeacon`
 *     abandon with reason `pagehide` so we still see drop-off when the
 *     user closes the tab. Beacons are tolerated as best-effort — the
 *     server-side session-timeout sweeper backstops the few that are
 *     dropped by the browser.
 *
 * All errors are swallowed and logged — analytics MUST NEVER affect the
 * checkout/payment flow (plan §1.13).
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import type { AnalyticsFlow } from 'pi-kiosk-shared';
import { logAnalyticsDevError } from 'pi-kiosk-shared/analyticsDevLog';
import {
  recordAnalyticsEmitDedup,
  shouldDedupAnalyticsEmit,
} from 'pi-kiosk-shared/analyticsEmitDedup';
import { isAnalyticsEventAllowedWithoutConsent } from 'pi-kiosk-shared/analyticsConsentAllowlist';
import { isAnalyticsEventName } from 'pi-kiosk-shared/analyticsEvents';
import {
  getOrCreateAnonymousKey,
} from './analyticsAnonymousStorage.js';
import {
  recordAnalyticsSessionId,
} from './analyticsSessionStorage.js';
import { CONSENT_VERSION } from '../consent/constants.js';
import {
  createCustomerAnalyticsClient,
  type CustomerAnalyticsClient,
  type CustomerEmitEventInput,
  type CustomerStartSessionInput,
  type CustomerStartSessionResult,
} from './customerAnalyticsClient.js';
import type { ApiClient } from '../api/ApiClient.js';
import type { AnalyticsEventName } from 'pi-kiosk-shared/analyticsEvents';
import { customerLogger } from '../logging/customerLogger.js';
import {
  isAnalyticsSessionAuthError,
  isAnalyticsSessionClosedError,
  shouldEnqueueAnalyticsOutboxError,
} from 'pi-kiosk-shared/analyticsIngestOutboxPolicy';
import {
  createIndexedDbCustomerAnalyticsOutboxStorage,
  enqueueCustomerAnalyticsOutboxEvent,
  flushCustomerAnalyticsOutbox,
  type CustomerAnalyticsOutboxStorage,
} from './customerAnalyticsOutbox.js';
import {
  drainPendingCustomerAnalyticsEmits,
  enqueuePendingCustomerAnalyticsEmit,
} from './customerAnalyticsPendingEmits.js';
import { consumeDiscoveryAttributionContext } from '../../features/discovery/discoveryAttributionContext.js';
import {
  mergeCustomerAcquisitionSource,
  resolveCustomerAcquisitionSource,
  type CustomerAcquisitionSource,
} from '../../features/discovery/resolveCustomerAcquisitionSource.js';

export interface CustomerAnalyticsManager {
  /** Current sessionId, or `null` if no session has started yet. */
  getSessionId: () => string | null;
  /** Emit. Starts a session lazily on first call. Callers may ignore the promise. */
  track: (input: CustomerEmitEventInput) => Promise<void>;
  /** Best-effort `session_completed` then resets. */
  completeSession: () => Promise<void>;
  /** Best-effort `session_abandoned` then resets. */
  abandonSession: (reason?: string) => Promise<void>;
  /**
   * Optional — bind a pre-existing sessionId (e.g. the checkout session
   * publicId) before any `track()` call so the analytics session and the
   * commerce session share the same UUID.
   */
  bindSessionId: (sessionId: string) => void;
}

export interface UseCustomerAnalyticsManagerArgs {
  readonly api: ApiClient | null;
  readonly tenantCode: string;
  readonly apiBaseUrl: string;
  /** Optional kiosk linkage when the PWA was opened from a kiosk QR. */
  readonly kioskId?: number | null;
  /** RETAIL or DONATION; passed through to startSession + every emit. */
  readonly flow?: AnalyticsFlow | null;
  /** Disable unload beacons (used in unit tests / SSR). */
  readonly disableUnloadBeacons?: boolean;
  /** When false (default), all v1 events may be tracked regardless of cookie consent. */
  readonly consentIngestGateEnabled?: boolean;
  /** Cookie-banner analytics toggle; used only when `consentIngestGateEnabled` is true. */
  readonly analyticsConsentGranted?: boolean;
  /** Consent latch version (D6 — must match backend policy). */
  readonly analyticsConsentVersion?: string;
  readonly analyticsConsentDecidedAtMs?: number | null;
  /** Injectable outbox storage (unit tests). Defaults to IndexedDB in browser. */
  readonly outboxStorage?: CustomerAnalyticsOutboxStorage | null;
}

function resolveDefaultCustomerAnalyticsOutboxStorage(): CustomerAnalyticsOutboxStorage | null {
  if (typeof indexedDB === 'undefined') {
    return null;
  }
  if (typeof process !== 'undefined' && process.env.JEST_WORKER_ID !== undefined) {
    return null;
  }
  return createIndexedDbCustomerAnalyticsOutboxStorage();
}

export function useCustomerAnalyticsManager({
  api,
  tenantCode,
  apiBaseUrl,
  kioskId,
  flow,
  disableUnloadBeacons,
  consentIngestGateEnabled,
  analyticsConsentGranted,
  analyticsConsentVersion,
  analyticsConsentDecidedAtMs,
  outboxStorage,
}: UseCustomerAnalyticsManagerArgs): CustomerAnalyticsManager {
  const client = useMemo(
    () => (api !== null ? createCustomerAnalyticsClient(api) : null),
    [api],
  );
  const clientRef = useRef<CustomerAnalyticsClient | null>(null);
  useLayoutEffect(() => {
    clientRef.current = client;
  }, [client]);
  const sessionIdRef = useRef<string | null>(null);
  const startPromiseRef = useRef<Promise<string> | null>(null);
  const flowRef = useRef<AnalyticsFlow | null>(flow ?? null);
  const kioskIdRef = useRef<number | null>(kioskId ?? null);
  const tenantRef = useRef<string>(tenantCode);
  const baseRef = useRef<string>(apiBaseUrl);
  const preSessionIdRef = useRef<string | null>(null);
  const anonymousKeyRef = useRef<string | null>(null);
  const latchedAcquisitionSourceRef = useRef<CustomerAcquisitionSource | null>(null);
  const prevConsentGrantedRef = useRef<boolean>(false);
  const outboxStorageRef = useRef<CustomerAnalyticsOutboxStorage | null>(
    outboxStorage === undefined ? resolveDefaultCustomerAnalyticsOutboxStorage() : outboxStorage,
  );

  useEffect(() => {
    outboxStorageRef.current =
      outboxStorage === undefined ? resolveDefaultCustomerAnalyticsOutboxStorage() : outboxStorage;
  }, [outboxStorage]);

  const tryFlushOutbox = useCallback(async (): Promise<void> => {
    const client = clientRef.current;
    const storage = outboxStorageRef.current;
    if (!client || !storage) {
      return;
    }
    try {
      await flushCustomerAnalyticsOutbox({ storage, client });
    } catch (err) {
      customerLogger.warn('customer analytics outbox flush failed', err);
    }
  }, []);

  const consentVersion = analyticsConsentVersion ?? CONSENT_VERSION;

  const buildConsentMetadata = useCallback((): Record<string, unknown> => {
    if (analyticsConsentGranted !== true) {
      return {};
    }
    const decidedAtMs = analyticsConsentDecidedAtMs ?? Date.now();
    return {
      analyticsConsent: {
        version: consentVersion,
        decidedAtMs,
      },
    };
  }, [analyticsConsentGranted, analyticsConsentDecidedAtMs, consentVersion]);

  const resolveAnonymousKey = useCallback((): string | null => {
    if (analyticsConsentGranted !== true) {
      return null;
    }
    if (anonymousKeyRef.current !== null) {
      return anonymousKeyRef.current;
    }
    const key = getOrCreateAnonymousKey(tenantRef.current, {
      consentGranted: true,
    });
    anonymousKeyRef.current = key;
    return key;
  }, [analyticsConsentGranted]);

  useEffect(() => {
    flowRef.current = flow ?? null;
  }, [flow]);

  useEffect(() => {
    kioskIdRef.current = kioskId ?? null;
  }, [kioskId]);

  useEffect(() => {
    tenantRef.current = tenantCode;
  }, [tenantCode]);

  useEffect(() => {
    baseRef.current = apiBaseUrl;
  }, [apiBaseUrl]);

  const bindSessionId = useCallback((sessionId: string): void => {
    if (sessionIdRef.current === sessionId) {
      return;
    }
    if (sessionIdRef.current !== null) {
      return;
    }
    preSessionIdRef.current = sessionId;
  }, []);

  const clearSessionState = useCallback((): void => {
    sessionIdRef.current = null;
    preSessionIdRef.current = null;
    startPromiseRef.current = null;
    latchedAcquisitionSourceRef.current = null;
    clientRef.current?.resetSessionAuth();
  }, []);

  const resolveStartSessionAcquisition = useCallback((): {
    readonly source: CustomerAcquisitionSource;
    readonly metadata: Record<string, unknown>;
  } => {
    const discoveryAttribution = consumeDiscoveryAttributionContext(tenantRef.current);
    const urlParams =
      typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search)
        : new URLSearchParams();
    const documentReferrer =
      typeof document !== 'undefined' ? document.referrer : null;
    const resolved = resolveCustomerAcquisitionSource({
      urlSearchParams: urlParams,
      documentReferrer,
      hasValidDiscoveryAttribution: discoveryAttribution !== null,
    });
    const source = mergeCustomerAcquisitionSource(
      latchedAcquisitionSourceRef.current,
      resolved,
    );
    latchedAcquisitionSourceRef.current = source;
    const metadata: Record<string, unknown> = {
      ...buildConsentMetadata(),
    };
    if (discoveryAttribution !== null) {
      metadata.discovery_search_request_id = discoveryAttribution.searchRequestId;
    }
    return { source, metadata };
  }, [buildConsentMetadata]);

  const requestSessionStart = useCallback(
    async (input: CustomerStartSessionInput): Promise<CustomerStartSessionResult> => {
      const client = clientRef.current;
      if (client === null) {
        throw new Error('analytics_client_unavailable');
      }
      return client.startSession(input);
    },
    [],
  );

  const startSessionWithConsent = useCallback(
    async (sessionIdOverride?: string): Promise<string> => {
      const client = clientRef.current;
      if (client === null) {
        throw new Error('analytics_client_unavailable');
      }
      const anonymousKey = resolveAnonymousKey();
      const acquisition = resolveStartSessionAcquisition();
      const baseInput: CustomerStartSessionInput = {
        salesPointId: kioskIdRef.current,
        platform: 'MOBILE',
        flow: flowRef.current,
        source: acquisition.source,
        anonymousKey,
        metadata: acquisition.metadata,
      };

      let result = await requestSessionStart({
        ...baseInput,
        sessionId: sessionIdOverride ?? preSessionIdRef.current ?? undefined,
      });

      if (result.isClosed) {
        preSessionIdRef.current = null;
        client.resetSessionAuth();
        result = await requestSessionStart({
          ...baseInput,
          sessionId: undefined,
        });
        await client.emit(result.sessionId, {
          eventName: 'session_recovered' as AnalyticsEventName,
          screenName: 'analytics_manager',
          flow: flowRef.current,
          metadata: {
            recovery_reason: 'closed_session',
          },
          anonymousKey,
        });
      }

      sessionIdRef.current = result.sessionId;
      recordAnalyticsSessionId(tenantRef.current, result.sessionId);
      return result.sessionId;
    },
    [requestSessionStart, resolveAnonymousKey, resolveStartSessionAcquisition],
  );

  const canTrack = useCallback(
    (eventName: string): boolean => {
      if (consentIngestGateEnabled !== true) {
        return true;
      }
      if (isAnalyticsEventAllowedWithoutConsent(eventName)) {
        return true;
      }
      if (!isAnalyticsEventName(eventName)) {
        return false;
      }
      return analyticsConsentGranted === true;
    },
    [consentIngestGateEnabled, analyticsConsentGranted],
  );

  const ensureSession = useCallback(async (): Promise<string> => {
    if (sessionIdRef.current !== null) {
      return sessionIdRef.current;
    }
    if (startPromiseRef.current !== null) {
      return startPromiseRef.current;
    }
    const startPromise = startSessionWithConsent();
    startPromiseRef.current = startPromise;
    try {
      return await startPromise;
    } finally {
      startPromiseRef.current = null;
    }
  }, [startSessionWithConsent]);

  const emitTrackedEvent = useCallback(
    async (input: CustomerEmitEventInput): Promise<void> => {
      const sessionId = await ensureSession();
      const client = clientRef.current;
      if (client === null) {
        return;
      }
      await client.emit(sessionId, {
        ...input,
        flow: input.flow ?? flowRef.current,
        anonymousKey: resolveAnonymousKey(),
      });
      await tryFlushOutbox();
    },
    [ensureSession, resolveAnonymousKey, tryFlushOutbox],
  );

  const track = useCallback(
    async (input: CustomerEmitEventInput): Promise<void> => {
      if (!canTrack(input.eventName)) {
        return;
      }
      if (shouldDedupAnalyticsEmit({
        eventName: input.eventName,
        screenName: input.screenName,
      })) {
        return;
      }
      // Platform chrome without a real API tenant — queue until client exists
      // (jwt remap after login). Do not throw / log emit_failed noise.
      if (clientRef.current === null) {
        enqueuePendingCustomerAnalyticsEmit(input);
        return;
      }
      await (async (): Promise<void> => {
        try {
          await emitTrackedEvent(input);
          recordAnalyticsEmitDedup({
            eventName: input.eventName,
            screenName: input.screenName,
          });
        } catch (err) {
          if (isAnalyticsSessionClosedError(err)) {
            clearSessionState();
            try {
              await emitTrackedEvent(input);
              recordAnalyticsEmitDedup({
                eventName: input.eventName,
                screenName: input.screenName,
              });
            } catch (retryErr) {
              if (shouldEnqueueAnalyticsOutboxError(retryErr)) {
                const sessionId = sessionIdRef.current;
                const storage = outboxStorageRef.current;
                if (sessionId && storage) {
                  try {
                    await enqueueCustomerAnalyticsOutboxEvent({
                      storage,
                      sessionId,
                      payload: input,
                    });
                  } catch (queueErr) {
                    customerLogger.warn('customer analytics outbox enqueue failed', queueErr);
                  }
                }
              }
              logAnalyticsDevError('emit_failed', {
                eventName: input.eventName,
                reason: retryErr instanceof Error ? retryErr.message : String(retryErr),
              });
            }
            return;
          }
          if (isAnalyticsSessionAuthError(err)) {
            clearSessionState();
            logAnalyticsDevError('emit_failed', {
              eventName: input.eventName,
              reason: err instanceof Error ? err.message : String(err),
            });
            return;
          }
          if (shouldEnqueueAnalyticsOutboxError(err)) {
            const sessionId = sessionIdRef.current;
            const storage = outboxStorageRef.current;
            if (sessionId && storage) {
              try {
                await enqueueCustomerAnalyticsOutboxEvent({
                  storage,
                  sessionId,
                  payload: input,
                });
              } catch (queueErr) {
                customerLogger.warn('customer analytics outbox enqueue failed', queueErr);
              }
            }
          }
          logAnalyticsDevError('emit_failed', {
            eventName: input.eventName,
            reason: err instanceof Error ? err.message : String(err),
          });
        }
      })();
    },
    [canTrack, clearSessionState, emitTrackedEvent],
  );

  useEffect(() => {
    if (client === null) {
      return;
    }
    const queued = drainPendingCustomerAnalyticsEmits();
    for (const input of queued) {
      track(input);
    }
  }, [client, track]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const handleOnline = (): void => {
      void tryFlushOutbox();
    };
    window.addEventListener('online', handleOnline);
    return (): void => {
      window.removeEventListener('online', handleOnline);
    };
  }, [tryFlushOutbox]);

  useEffect(() => {
    const granted = analyticsConsentGranted === true;
    const wasGranted = prevConsentGrantedRef.current;
    prevConsentGrantedRef.current = granted;
    if (!granted) {
      anonymousKeyRef.current = null;
      return;
    }
    if (wasGranted) {
      return;
    }
    const currentSession = sessionIdRef.current ?? preSessionIdRef.current;
    if (currentSession === null) {
      return;
    }
    void (async (): Promise<void> => {
      try {
        await startSessionWithConsent(currentSession);
      } catch (err) {
        logAnalyticsDevError('mid_session_consent_patch_failed', {
          reason: err instanceof Error ? err.message : String(err),
        });
      }
    })();
  }, [analyticsConsentGranted, startSessionWithConsent]);

  const completeSession = useCallback(async (): Promise<void> => {
    const client = clientRef.current;
    const current = sessionIdRef.current;
    if (client === null || current === null) {
      return;
    }
    sessionIdRef.current = null;
    preSessionIdRef.current = null;
    try {
      await client.completeSession(current);
    } catch (err) {
      customerLogger.warn('customer analytics complete failed', err);
    }
  }, []);

  const abandonSession = useCallback(
    async (reason?: string): Promise<void> => {
      const client = clientRef.current;
      if (client === null) {
        return;
      }
      let current = sessionIdRef.current;
      if (current === null) {
        try {
          current = await ensureSession();
        } catch (err) {
          customerLogger.warn('customer analytics abandon ensureSession failed', err);
          return;
        }
      }
      sessionIdRef.current = null;
      preSessionIdRef.current = null;
      try {
        await client.abandonSession(current, reason ?? 'explicit');
      } catch (err) {
        customerLogger.warn('customer analytics abandon failed', err);
      }
    },
    [ensureSession],
  );

  useEffect(() => {
    if (disableUnloadBeacons === true) {
      return;
    }
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }
    const resetSessionStateOnUnload = (): void => {
      clearSessionState();
    };
    const fireBeacon = (reason: string): void => {
      const client = clientRef.current;
      const current = sessionIdRef.current;
      const tenant = tenantRef.current;
      const base = baseRef.current;
      if (client === null || current === null) {
        resetSessionStateOnUnload();
        return;
      }
      client.abandonSessionBeacon(tenant, base, current, reason);
      resetSessionStateOnUnload();
    };
    const handlePageHide = (): void => fireBeacon('pagehide');
    const handleVisibility = (): void => {
      if (document.visibilityState !== 'hidden') {
        return;
      }
      // E1: visibility abandon only after a started session (not preSession-only).
      if (sessionIdRef.current === null) {
        resetSessionStateOnUnload();
        return;
      }
      fireBeacon('visibility_hidden');
    };
    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('visibilitychange', handleVisibility);
    return (): void => {
      window.removeEventListener('pagehide', handlePageHide);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [clearSessionState, disableUnloadBeacons]);

  return useMemo<CustomerAnalyticsManager>(
    () => ({
      getSessionId: () => sessionIdRef.current,
      track,
      completeSession,
      abandonSession,
      bindSessionId,
    }),
    [track, completeSession, abandonSession, bindSessionId],
  );
}
